import { Box, Button, IconButton, Paper } from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import { useCallback, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import type { Dispatch } from "redux"
import { SortableAttachmentList, TabContainer } from ".."
import { AttachmentType } from "../../../models"
import {
  MapViewerPanel,
  attachmentEditorStartCreate,
  attachmentEditorStartEdit,
  mapViewerTogglePanel,
  pushMessage
} from "../../../redux"
import type { AppState, Attachment } from "../../../redux"
import { Header4 } from "../../atoms"

import "./map-attachement-side-panel.styles.scss"
import ArrowForward from "@material-ui/icons/ArrowForward"
import { useTranslation } from "react-i18next"

export interface IMapAttachmentSidePanelOwnProps {
  attachments: Attachment[]
  allowAuthorContentEditing?: boolean
  reorderAttachments: (attachments: Attachment[]) => void
  onDeleteAttachment: (attachment: Attachment) => void
}

export interface IMapAttachementSidePanelStateProps {
  highlightedAttachment?: Attachment
  closePanel: () => void
  createNewAttachment: (attachmentCount: number) => void
  editAttachment: (attachment: Attachment) => void
  pushMessage: (message: string) => void
}

function MapAttachementSidePanelView(
  props: IMapAttachmentSidePanelOwnProps & IMapAttachementSidePanelStateProps
): ReactElement {
  const {
    attachments,
    allowAuthorContentEditing,
    highlightedAttachment,
    closePanel,
    createNewAttachment,
    editAttachment,
    reorderAttachments,
    onDeleteAttachment,
    pushMessage
  } = props
  const { t } = useTranslation("map")

  const [activeTab, setActiveTab] = useState<number>(0)

  const getActiveType: () => AttachmentType | undefined = useCallback<() => AttachmentType | undefined>(() => {
    switch (activeTab) {
      case 1:
        return AttachmentType.Image
      case 2:
        return AttachmentType.Video
      case 3:
        return AttachmentType.Note
      default:
        return undefined
    }
  }, [activeTab])

  function handleDeleteAttachment(attachment: Attachment): void {
    onDeleteAttachment(attachment)
    pushMessage(t("attachments.attachmentRemoved"))
  }

  return (
    <Box className="map-attachement-side-panel">
      <Paper elevation={3} className="map-attachement-side-panel-container">
        <Box className="header">
          <Header4>
            {t("attachments.attachmentsAmount", {
              amount: attachments.length
            })}
          </Header4>
          <IconButton onClick={closePanel}>
            <ArrowForward fontSize="default" />
          </IconButton>
        </Box>
        <Button
          onClick={() => createNewAttachment(attachments.length)}
          variant="outlined"
          color="primary"
          size="small"
          className="add-attachment-button"
        >
          <AddIcon /> {t("attachments.addAttachment")}
        </Button>
        <TabContainer
          tabs={[
            t("attachments.all"),
            t("attachments.image.imagesTitle"),
            t("attachments.video.videosTitle"),
            t("attachments.note.notesTitle")
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <SortableAttachmentList
          activeType={getActiveType()}
          attachments={attachments}
          highlightedAttachment={highlightedAttachment}
          allowAuthorContentEditing={allowAuthorContentEditing}
          reorderAttachments={reorderAttachments}
          onDeleteAttachment={handleDeleteAttachment}
          onEditAttachment={editAttachment}
        />
      </Paper>
    </Box>
  )
}

function mapStateToProps(state: AppState) {
  return {
    highlightedAttachment: state.attachmentsEditor.highlightAttachment
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    closePanel: () => dispatch(mapViewerTogglePanel(MapViewerPanel.Attachement, false)),
    createNewAttachment: (attachmentCount: number) => dispatch(attachmentEditorStartCreate(attachmentCount)),
    editAttachment: (attachment: Attachment) => dispatch(attachmentEditorStartEdit(attachment)),
    pushMessage: (message: string) => dispatch(pushMessage(message))
  }
}

export const MapAttachementSidePanel = connect(mapStateToProps, mapDispatchToProps)(MapAttachementSidePanelView)
